import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isValidPhoneNumber, parsePhoneNumberWithError } from 'libphonenumber-js';
import { Upload } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { Checkbox } from '~/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '~/components/ui/dialog';
import { Input } from '~/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '~/components/ui/select';
import { api } from '~/lib/api';
import { i18n } from '~/lib/i18n';

// ── Types ────────────────────────────────────────────────────────────────────

type Step = 1 | 2 | 3 | 'success';

type ColMap = { nameCol: number | null; emailCol: number | null; phoneCol: number | null };

type ReviewRow = {
  name: string;
  email: string;
  phone: string;
  nameError: string | null;
  emailError: string | null;
  phoneError: string | null;
  parsedPhoneNumber: string | null;
  parsedPhoneCountryCode: string | null;
};

type ImportResult = { created: number; conflict: number; error: number };

// ── CSV parser ───────────────────────────────────────────────────────────────

function parseCsv(text: string): string[][] {
  return text
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .filter((line) => line.trim())
    .map((line) => {
      const cells: string[] = [];
      const fieldPattern = /("(?:[^"]|"")*"|[^,]*)(,|$)/gy;
      let match: RegExpExecArray | null;
      while ((match = fieldPattern.exec(line)) !== null) {
        const field = match[1] ?? '';
        cells.push(field.startsWith('"') ? field.slice(1, -1).replace(/""/g, '"') : field);
        if (match[2] === '') break;
      }
      return cells;
    });
}

// ── Column auto-detection ────────────────────────────────────────────────────

const NAME_KW = ['name', 'fullname', 'full name', 'nom', 'nombre'];
const EMAIL_KW = ['email', 'e-mail', 'mail', 'correo'];
const PHONE_KW = ['phone', 'tel', 'telephone', 'mobile', 'cell', 'número', 'movil'];

function detectCols(headers: string[], samples: string[][]): ColMap {
  const lh = headers.map((h) => h.toLowerCase().trim());
  const cm: ColMap = { nameCol: null, emailCol: null, phoneCol: null };

  for (let i = 0; i < lh.length; i++) {
    const h = lh[i] ?? '';
    if (cm.emailCol === null && EMAIL_KW.some((k) => h.includes(k))) cm.emailCol = i;
    else if (cm.phoneCol === null && PHONE_KW.some((k) => h.includes(k))) cm.phoneCol = i;
    else if (cm.nameCol === null && NAME_KW.some((k) => h.includes(k))) cm.nameCol = i;
  }

  for (let i = 0; i < headers.length; i++) {
    const vals = samples.map((r) => (r[i] ?? '').trim());
    if (cm.emailCol === null && vals.some((v) => v.includes('@'))) {
      cm.emailCol = i;
    } else if (cm.phoneCol === null && vals.some((v) => /^\+?\d[\d\s\-()]{5,}$/.test(v))) {
      cm.phoneCol = i;
    }
  }

  if (cm.nameCol === null) {
    for (let i = 0; i < headers.length; i++) {
      if (i !== cm.emailCol && i !== cm.phoneCol) {
        cm.nameCol = i;
        break;
      }
    }
  }

  return cm;
}

// ── Row validator ────────────────────────────────────────────────────────────

function validateRow(row: ReviewRow): ReviewRow {
  const nameError =
    row.name.trim().length === 0 ? i18n.t('applications.importDialog.errors.nameRequired') : null;
  const emailError = !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email.trim())
    ? i18n.t('applications.importDialog.errors.emailInvalid')
    : null;

  let phoneError: string | null = null;
  let parsedPhoneNumber: string | null = null;
  let parsedPhoneCountryCode: string | null = null;

  try {
    const trimmed = row.phone.trim();
    if (!isValidPhoneNumber(trimmed)) throw new Error();
    const parsed = parsePhoneNumberWithError(trimmed);
    parsedPhoneNumber = parsed.nationalNumber;
    parsedPhoneCountryCode = `+${parsed.countryCallingCode}`;
  } catch {
    phoneError = i18n.t('applications.importDialog.errors.phoneInvalid');
  }

  return { ...row, nameError, emailError, phoneError, parsedPhoneNumber, parsedPhoneCountryCode };
}

function markDuplicates(rows: ReviewRow[]): ReviewRow[] {
  const counts = new Map<string, number>();
  for (const row of rows) {
    const key = row.email.trim().toLowerCase();
    if (key) counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return rows.map((row) => {
    const key = row.email.trim().toLowerCase();
    if (!row.emailError && key && (counts.get(key) ?? 0) > 1) {
      return { ...row, emailError: i18n.t('applications.importDialog.errors.emailDuplicate') };
    }
    return row;
  });
}

function validateAll(rows: ReviewRow[]): ReviewRow[] {
  return markDuplicates(rows.map(validateRow));
}

// ── Props ────────────────────────────────────────────────────────────────────

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string;
  deptId: string;
  jobId: string;
};

// ── Component ────────────────────────────────────────────────────────────────

export function ImportCandidatesDialog({
  open,
  onOpenChange,
  orgId,
  deptId,
  jobId
}: Props): React.ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>(1);
  const [rawRows, setRawRows] = useState<string[][]>([]);
  const [hasHeader, setHasHeader] = useState(true);
  const [colMap, setColMap] = useState<ColMap>({ nameCol: null, emailCol: null, phoneCol: null });
  const [reviewRows, setReviewRows] = useState<ReviewRow[]>([]);
  const [skipInvalid, setSkipInvalid] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);

  function resetState(): void {
    setStep(1);
    setRawRows([]);
    setHasHeader(true);
    setColMap({ nameCol: null, emailCol: null, phoneCol: null });
    setReviewRows([]);
    setSkipInvalid(false);
    setResult(null);
  }

  const handleFile = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const parsed = parseCsv(text);
      setRawRows(parsed);
      const headerRow = parsed[0] ?? [];
      setColMap(detectCols(headerRow, parsed.slice(1, 6)));
      setStep(2);
    };
    reader.readAsText(file);
  }, []);

  function buildReviewRows(): void {
    const dataRows = (hasHeader ? rawRows.slice(1) : rawRows).filter((r) =>
      r.some((c) => c.trim())
    );
    const rows: ReviewRow[] = dataRows.map((row) => ({
      name: colMap.nameCol !== null ? (row[colMap.nameCol] ?? '').trim() : '',
      email: colMap.emailCol !== null ? (row[colMap.emailCol] ?? '').trim() : '',
      phone: colMap.phoneCol !== null ? (row[colMap.phoneCol] ?? '').trim() : '',
      nameError: null,
      emailError: null,
      phoneError: null,
      parsedPhoneNumber: null,
      parsedPhoneCountryCode: null
    }));
    setReviewRows(validateAll(rows));
    setStep(3);
  }

  const importMutation = useMutation({
    mutationFn: async (rows: ReviewRow[]) => {
      const res = await api.api.org[':orgId'].department[':deptId'].jobs[
        ':jobId'
      ].applications.bulk.$post({
        param: { orgId, deptId, jobId },
        json: {
          candidates: rows.map((r) => ({
            name: r.name,
            email: r.email,
            phoneNumber: r.parsedPhoneNumber ?? '',
            phoneCountryCode: r.parsedPhoneCountryCode ?? ''
          }))
        }
      });
      return res.json();
    },
    onSuccess: (data) => {
      const r = data.results;
      setResult({
        created: r.filter((x) => x.status === 'created').length,
        conflict: r.filter((x) => x.status === 'conflict').length,
        error: r.filter((x) => x.status === 'error').length
      });
      setStep('success');
      void queryClient.invalidateQueries({ queryKey: ['applications', orgId, deptId, jobId] });
    }
  });

  const colHeaders = hasHeader
    ? (rawRows[0] ?? [])
    : (rawRows[0] ?? []).map(
        (_, i) => `${t('applications.importDialog.mapping.column')} ${String(i + 1)}`
      );

  const validRows = reviewRows.filter((r) => !r.nameError && !r.emailError && !r.phoneError);
  const invalidCount = reviewRows.filter((r) => r.nameError ?? r.emailError ?? r.phoneError).length;
  const allValid = invalidCount === 0 && reviewRows.length > 0;
  const canImport = allValid || (skipInvalid && validRows.length > 0);

  const mappingFields: Array<[keyof ColMap, string]> = [
    ['nameCol', t('applications.importDialog.mapping.nameColumn')],
    ['emailCol', t('applications.importDialog.mapping.emailColumn')],
    ['phoneCol', t('applications.importDialog.mapping.phoneColumn')]
  ];

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) resetState();
        onOpenChange(v);
      }}
    >
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>{t('applications.importDialog.title')}</DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="flex flex-col items-center gap-4 py-6">
            <div
              onDrop={(e) => {
                e.preventDefault();
                const f = e.dataTransfer.files[0];
                if (f) handleFile(f);
              }}
              onDragOver={(e) => {
                e.preventDefault();
              }}
              className="flex w-full flex-col items-center gap-3 rounded-lg border-2 border-dashed border-border p-8 text-center transition-colors hover:border-primary/50"
            >
              <Upload className="size-8 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">{t('applications.importDialog.upload.hint')}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {t('applications.importDialog.upload.csvOnly')}
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
                {t('applications.importDialog.upload.browse')}
              </Button>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFile(f);
                e.target.value = '';
              }}
            />
          </div>
        )}

        {step === 2 && (
          <>
            <div className="flex flex-col gap-4 py-2">
              <label className="flex cursor-pointer items-center gap-2 text-sm">
                <Checkbox
                  checked={hasHeader}
                  onCheckedChange={(v) => {
                    setHasHeader(v);
                  }}
                />
                {t('applications.importDialog.mapping.headerRow')}
              </label>

              <div className="grid grid-cols-3 gap-3">
                {mappingFields.map(([key, label]) => (
                  <div key={key} className="flex flex-col gap-1.5">
                    <p className="text-sm font-medium">{label}</p>
                    <Select
                      value={colMap[key]?.toString() ?? ''}
                      onValueChange={(v) => {
                        setColMap((m) => ({ ...m, [key]: v === '' ? null : Number(v) }));
                      }}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue>
                          {colMap[key] !== null
                            ? (colHeaders[colMap[key]] ?? `Col ${String(colMap[key] + 1)}`)
                            : t('applications.importDialog.mapping.notMapped')}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">
                          {t('applications.importDialog.mapping.notMapped')}
                        </SelectItem>
                        {colHeaders.map((h, i) => (
                          <SelectItem key={i} value={i.toString()}>
                            {h || `Col ${String(i + 1)}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>

              <div>
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {t('applications.importDialog.mapping.preview')}
                </p>
                <div className="overflow-x-auto rounded border border-border text-xs">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        {(rawRows[0] ?? []).map((_, ci) => (
                          <th key={ci} className="px-2 py-1 text-left font-normal">
                            {colMap.nameCol === ci && (
                              <span className="rounded bg-blue-100 px-1.5 py-0.5 text-xs font-medium text-blue-700">
                                {t('applications.importDialog.review.colName')}
                              </span>
                            )}
                            {colMap.emailCol === ci && (
                              <span className="rounded bg-violet-100 px-1.5 py-0.5 text-xs font-medium text-violet-700">
                                {t('applications.importDialog.review.colEmail')}
                              </span>
                            )}
                            {colMap.phoneCol === ci && (
                              <span className="rounded bg-green-100 px-1.5 py-0.5 text-xs font-medium text-green-700">
                                {t('applications.importDialog.review.colPhone')}
                              </span>
                            )}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(hasHeader ? rawRows.slice(0, 6) : rawRows.slice(0, 5)).map((row, ri) => (
                        <tr
                          key={ri}
                          className={
                            ri === 0 && hasHeader ? 'bg-muted font-medium' : 'even:bg-muted/30'
                          }
                        >
                          {row.map((cell, ci) => (
                            <td
                              key={ci}
                              className={`px-2 py-1.5 ${
                                colMap.nameCol === ci
                                  ? 'bg-blue-50/60'
                                  : colMap.emailCol === ci
                                    ? 'bg-violet-50/60'
                                    : colMap.phoneCol === ci
                                      ? 'bg-green-50/60'
                                      : ''
                              }`}
                            >
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setStep(1);
                }}
              >
                {t('applications.importDialog.back')}
              </Button>
              <Button
                onClick={buildReviewRows}
                disabled={
                  colMap.nameCol === null || colMap.emailCol === null || colMap.phoneCol === null
                }
              >
                {t('applications.importDialog.next')}
              </Button>
            </DialogFooter>
          </>
        )}

        {step === 3 && (
          <>
            <div className="flex flex-col gap-3 py-2">
              <p className="text-sm text-muted-foreground">
                {allValid
                  ? t('applications.importDialog.review.allValid', { count: reviewRows.length })
                  : t('applications.importDialog.review.summary', {
                      valid: validRows.length,
                      invalid: invalidCount
                    })}
              </p>

              <div className="max-h-64 overflow-y-auto rounded border border-border text-xs">
                <table className="w-full">
                  <thead className="sticky top-0 bg-muted">
                    <tr>
                      <th className="px-2 py-1.5 text-left font-medium">
                        {t('applications.importDialog.review.colName')}
                      </th>
                      <th className="px-2 py-1.5 text-left font-medium">
                        {t('applications.importDialog.review.colEmail')}
                      </th>
                      <th className="px-2 py-1.5 text-left font-medium">
                        {t('applications.importDialog.review.colPhone')}
                      </th>
                      <th className="px-2 py-1.5 text-left font-medium">
                        {t('applications.importDialog.review.colStatus')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {reviewRows.map((row, ri) => {
                      const hasError = row.nameError ?? row.emailError ?? row.phoneError;
                      return (
                        <tr key={ri} className={hasError ? 'bg-destructive/5' : 'even:bg-muted/20'}>
                          <td className="px-1 py-1">
                            <Input
                              className="h-6 text-xs"
                              value={row.name}
                              onChange={(e) => {
                                setReviewRows((rows) =>
                                  rows.map((r, i) =>
                                    i === ri ? { ...r, name: e.target.value } : r
                                  )
                                );
                              }}
                            />
                            {row.nameError && (
                              <p className="mt-0.5 text-destructive">{row.nameError}</p>
                            )}
                          </td>
                          <td className="px-1 py-1">
                            <Input
                              className="h-6 text-xs"
                              value={row.email}
                              onChange={(e) => {
                                setReviewRows((rows) =>
                                  rows.map((r, i) =>
                                    i === ri ? { ...r, email: e.target.value } : r
                                  )
                                );
                              }}
                            />
                            {row.emailError && (
                              <p className="mt-0.5 text-destructive">{row.emailError}</p>
                            )}
                          </td>
                          <td className="px-1 py-1">
                            <Input
                              className="h-6 text-xs"
                              value={row.phone}
                              onChange={(e) => {
                                setReviewRows((rows) =>
                                  rows.map((r, i) =>
                                    i === ri ? { ...r, phone: e.target.value } : r
                                  )
                                );
                              }}
                            />
                            {row.phoneError && (
                              <p className="mt-0.5 text-destructive">{row.phoneError}</p>
                            )}
                          </td>
                          <td className="px-2 py-1 text-center">
                            {hasError ? (
                              <span className="text-destructive">✗</span>
                            ) : (
                              <span className="text-green-600">✓</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {!allValid && validRows.length > 0 && (
                <label className="flex cursor-pointer items-center gap-2 text-sm">
                  <Checkbox
                    checked={skipInvalid}
                    onCheckedChange={(v) => {
                      setSkipInvalid(v);
                    }}
                  />
                  {t('applications.importDialog.review.skipInvalid', { count: validRows.length })}
                </label>
              )}

              {importMutation.isError && (
                <p className="text-xs text-destructive">{importMutation.error.message}</p>
              )}
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setStep(2);
                }}
              >
                {t('applications.importDialog.back')}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setReviewRows((rows) => validateAll(rows));
                }}
              >
                {t('applications.importDialog.review.recheck')}
              </Button>
              <Button
                onClick={() => {
                  const rechecked = validateAll(reviewRows);
                  setReviewRows(rechecked);
                  const valid = rechecked.filter(
                    (r) => !r.nameError && !r.emailError && !r.phoneError
                  );
                  const hasInvalid = rechecked.some(
                    (r) => r.nameError ?? r.emailError ?? r.phoneError
                  );
                  if (valid.length === 0 || (hasInvalid && !skipInvalid)) return;
                  importMutation.mutate(valid);
                }}
                disabled={!canImport || importMutation.isPending}
              >
                {t('applications.importDialog.review.import', { count: validRows.length })}
              </Button>
            </DialogFooter>
          </>
        )}

        {step === 'success' && result && (
          <>
            <div className="flex flex-col gap-2 py-6">
              <p className="font-medium">{t('applications.importDialog.success.title')}</p>
              <p className="text-sm text-muted-foreground">
                {t('applications.importDialog.success.body', {
                  created: result.created,
                  conflict: result.conflict,
                  error: result.error
                })}
              </p>
            </div>
            <DialogFooter>
              <Button
                onClick={() => {
                  resetState();
                  onOpenChange(false);
                }}
              >
                {t('applications.importDialog.success.done')}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
